import { useState, useMemo, useEffect } from 'react';
import type { SmartMoneyWallet } from '../api/bipApi';
import { FALLBACK_AVATAR_LOGO } from '../constants';
import Tooltip from './Tooltip';

type SortKey = 'followers' | 'tokenAmount' | 'avgEntryMC' | 'holdDuration' | 'performance';
type SortDir = 'asc' | 'desc';

interface SmartMoneyTableProps {
  text: string;
  table: SmartMoneyWallet[];
  animationDelay?: string;
}

function formatTokenAmount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function parseAvgEntryMC(s: string): number {
  const raw = s.replace(/^\$|\s/g, '').trim();
  if (!raw || raw === '—') return Number.NaN;
  const num = parseFloat(raw.replace(/[KM]$/i, ''));
  if (Number.isNaN(num)) return Number.NaN;
  if (/M$/i.test(raw)) return num * 1_000_000;
  if (/K$/i.test(raw)) return num * 1_000;
  return num;
}

function parseHoldDuration(s: string): number {
  const raw = (s || '').trim();
  if (!raw || raw === '—') return Number.NaN;
  const m = raw.match(/^(\d+(?:\.\d+)?)\s*(d|h|m|s)?$/i);
  if (!m) return Number.NaN;
  const n = parseFloat(m[1]);
  const unit = (m[2] || '').toLowerCase();
  if (unit === 'd') return n * 86400;
  if (unit === 'h') return n * 3600;
  if (unit === 'm') return n * 60;
  return n;
}

function performanceRank(p: string): number {
  const v = (p || '').toUpperCase();
  if (v === 'HIGH_CANDIDATE' || v === 'HIGH') return 3;
  if (v === 'MEDIUM') return 2;
  if (v === 'LOW') return 1;
  return 0;
}

const MIN_CONVICTION_DISPLAY = 0.01; // 0.00%일 수밖에 없을 때 표기용

/** Replace long conviction percentage in description text with 2-decimal version for display. */
function formatDescriptionConviction(text: string): string {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/top\s+([\d.]+)%\s*conviction/gi, (_, pct) => {
    const num = parseFloat(pct);
    if (Number.isNaN(num)) return `top ${pct}% conviction`;
    const display = num === 0 || num.toFixed(2) === '0.00' ? MIN_CONVICTION_DISPLAY : num;
    return `top ${display.toFixed(2)}% conviction`;
  });
}

/** Parse the 4 summary numbers from Smart Money template text. Returns null if pattern doesn't match. */
function parseSmartMoneySummary(text: string): {
  wallets: number;
  invested: string;
  tokensHeld: string;
  convictionPercent: string;
} | null {
  if (!text || typeof text !== 'string') return null;
  // e.g. "64 of my smart money wallets, invested $99.41K, and are currently holding 86.64M tokens, betting with top 0.02400408580183861% conviction."
  const walletsMatch = text.match(/(\d+)\s+of my smart money wallets/i);
  const investedMatch = text.match(/invested\s+\$([\d,.]+[KMB]?)/i);
  const tokensMatch = text.match(/holding\s+([\d,.]+[KMB]?)\s*tokens/i);
  const convictionMatch = text.match(/top\s+([\d.]+)%\s*conviction/i);
  if (!walletsMatch || !investedMatch || !tokensMatch || !convictionMatch) return null;
  const convictionNum = parseFloat(convictionMatch[1]);
  const convictionPercent = Number.isNaN(convictionNum)
    ? '—'
    : convictionNum === 0 || convictionNum.toFixed(2) === '0.00'
      ? `${MIN_CONVICTION_DISPLAY}%`
      : `${convictionNum.toFixed(2)}%`;
  return {
    wallets: parseInt(walletsMatch[1], 10),
    invested: `$${investedMatch[1]}`,
    tokensHeld: tokensMatch[1],
    convictionPercent,
  };
}

function PerformanceBadge({ value }: { value: string }) {
  if (!value || value === '—') return <span className="sm-perf sm-perf--empty sm-cell-empty">—</span>;
  const v = value.toUpperCase();
  const cls =
    v === 'HIGH_CANDIDATE'
      ? 'sm-perf sm-perf--high'
      : v === 'MEDIUM'
        ? 'sm-perf sm-perf--medium'
        : 'sm-perf sm-perf--low';
  const label = v === 'HIGH_CANDIDATE' ? 'HIGH' : value;
  return <span className={cls}>{label}</span>;
}

function SortHeader({
  label,
  sortKey,
  colClass,
  currentSort,
  currentDir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  colClass: string;
  currentSort: SortKey | null;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return (
    <button
      type="button"
      className={`${colClass} sm-col-sortable ${isActive ? 'sm-col-sortable--active' : ''}`}
      onClick={() => onSort(sortKey)}
      aria-label={isActive ? `Sort by ${label} ${currentDir === 'asc' ? 'ascending' : 'descending'}, click to reverse` : `Sort by ${label}`}
    >
      <span>{label}</span>
      {isActive && (
        <span className="sm-sort-arrow" aria-hidden>
          {currentDir === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );
}

const ROWS_PER_PAGE = 10;
const INITIAL_VISIBLE = 10;

export default function SmartMoneyTable({ text, table, animationDelay = '0.2s' }: SmartMoneyTableProps) {
  const [sortBy, setSortBy] = useState<SortKey | null>('followers');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [table.length]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      if (sortDir === 'desc') {
        setSortDir('asc');
      } else {
        setSortBy(null);
        setSortDir('desc');
      }
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const sortedTable = useMemo(() => {
    if (!sortBy) return table;
    const list = [...table];
    list.sort((a, b) => {
      let aVal: number, bVal: number;
      switch (sortBy) {
        case 'followers':
          aVal = a.twitterFollowers ?? 0;
          bVal = b.twitterFollowers ?? 0;
          break;
        case 'tokenAmount':
          aVal = a.tokenAmount ?? 0;
          bVal = b.tokenAmount ?? 0;
          break;
        case 'avgEntryMC':
          aVal = parseAvgEntryMC(a.avgEntryMC);
          bVal = parseAvgEntryMC(b.avgEntryMC);
          break;
        case 'holdDuration':
          aVal = parseHoldDuration(a.avgHoldingDuration);
          bVal = parseHoldDuration(b.avgHoldingDuration);
          break;
        case 'performance':
          aVal = performanceRank(a.performance);
          bVal = performanceRank(b.performance);
          break;
        default:
          return 0;
      }
      const aNaN = Number.isNaN(aVal);
      const bNaN = Number.isNaN(bVal);
      if (aNaN && bNaN) return 0;
      if (aNaN) return sortDir === 'asc' ? 1 : -1;
      if (bNaN) return sortDir === 'asc' ? -1 : 1;
      const cmp = aVal - bVal;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [table, sortBy, sortDir]);

  const visibleRows = useMemo(
    () => sortedTable.slice(0, visibleCount),
    [sortedTable, visibleCount],
  );
  const hasMore = sortedTable.length > visibleCount;

  return (
    <section className="result-section fade-in" id="section-smart-money" style={{ animationDelay }}>
      <div className="section-title-block">
        <h2 className="section-title">Smart Money Tracking</h2>
      </div>
      <p className="section-description">{formatDescriptionConviction(text)}</p>

      {(() => {
        const summary = parseSmartMoneySummary(text);
        if (!summary) return null;
        return (
          <div className="smart-money-dashboard">
            <div className="smart-money-dashboard-item">
              <span className="smart-money-dashboard-value">{summary.wallets.toLocaleString()}</span>
              <span className="smart-money-dashboard-label">Wallets</span>
            </div>
            <div className="smart-money-dashboard-item">
              <span className="smart-money-dashboard-value">{summary.invested}</span>
              <span className="smart-money-dashboard-label">Invested</span>
            </div>
            <div className="smart-money-dashboard-item">
              <span className="smart-money-dashboard-value">{summary.tokensHeld}</span>
              <span className="smart-money-dashboard-label">Tokens Held</span>
            </div>
            <div className="smart-money-dashboard-item">
              <span className="smart-money-dashboard-value">{summary.convictionPercent}</span>
              <Tooltip
                content="Percentile rank of this token's cumulative conviction vs. historical tokens at the same elapsed time from pool creation. Lower % means better rank (e.g. 0.02% = top 0.02% of all tokens)."
                position="top"
              >
                <span className="smart-money-dashboard-label smart-money-dashboard-label--with-tooltip" aria-label="Explanation of Top Conviction">
                  Top Conviction
                  <span className="smart-money-dashboard-tooltip-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                  </span>
                </span>
              </Tooltip>
            </div>
          </div>
        );
      })()}

      <div className="smart-money-table">
        <div className="smart-money-header">
          <div className="sm-col-wallet">Wallet</div>
          <div className="sm-col-profile-span">X Profile</div>
          <SortHeader
            label="Followers"
            sortKey="followers"
            colClass="sm-col-followers"
            currentSort={sortBy}
            currentDir={sortDir}
            onSort={handleSort}
          />
          <SortHeader
            label="Performance"
            sortKey="performance"
            colClass="sm-col-performance"
            currentSort={sortBy}
            currentDir={sortDir}
            onSort={handleSort}
          />
          <SortHeader
            label="Token Amount"
            sortKey="tokenAmount"
            colClass="sm-col-amount"
            currentSort={sortBy}
            currentDir={sortDir}
            onSort={handleSort}
          />
          <SortHeader
            label="Avg Entry MC"
            sortKey="avgEntryMC"
            colClass="sm-col-mc"
            currentSort={sortBy}
            currentDir={sortDir}
            onSort={handleSort}
          />
          <SortHeader
            label="Hold Duration"
            sortKey="holdDuration"
            colClass="sm-col-duration"
            currentSort={sortBy}
            currentDir={sortDir}
            onSort={handleSort}
          />
          <div className="sm-col-pnl">PnL</div>
          <div className="sm-col-winrate">Win Rate</div>
          <div className="sm-col-scalping">Scalping</div>
        </div>

        <div className="smart-money-body">
          {visibleRows.map((wallet, index) => {
            const isLoadMoreRow = index >= INITIAL_VISIBLE;
            const delay = isLoadMoreRow
              ? 0.05 + (index - INITIAL_VISIBLE) * 0.02
              : parseFloat(animationDelay) + 0.1 + index * 0.05;
            return (
            <div
              key={`${wallet.wallet}-${index}`}
              className="smart-money-row"
              style={{ animationDelay: `${delay}s` }}
            >
              <div className="sm-col-wallet">
                <span className="wallet-address-text">{wallet.wallet}</span>
              </div>
              <div className="sm-col-pfp">
                {wallet.twitterPfp && wallet.twitterUsername?.trim() ? (
                  <img
                    src={wallet.twitterPfp}
                    alt={wallet.twitterUsername}
                    className="twitter-pfp"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_AVATAR_LOGO;
                    }}
                  />
                ) : <span className="" style={{
                  fontSize: '0.7rem',
                  color: '#fff',
                  fontWeight: '500',
                  letterSpacing: '0.03em',
                  whiteSpace: 'nowrap',
                }}>None</span>}
              </div>
              <div className="sm-col-twitter">
                {wallet.twitterUsername?.trim() ? (
                  <a
                    href={`https://x.com/${wallet.twitterUsername.replace(/^@/, '').trim()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wallet-twitter-text"
                  >
                    {wallet.twitterUsername === "—" ? "" : wallet.twitterUsername}
                  </a>
                ) : (
                  <span className="sm-cell-empty"></span>
                )}
              </div>
              <div className="sm-col-followers">
                <span className="wallet-stat-text">
                  {wallet.twitterFollowers > 0
                    ? wallet.twitterFollowers.toLocaleString()
                    : <span className="sm-cell-empty">—</span>}
                </span>
              </div>
              <div className="sm-col-performance">
                <PerformanceBadge value={wallet.performance} />
              </div>
              <div className="sm-col-amount">
                <span className="wallet-stat-text">
                  {formatTokenAmount(wallet.tokenAmount)}
                </span>
              </div>
              <div className="sm-col-mc">
                <span className="wallet-stat-text">
                  {wallet.avgEntryMC === '—' || !wallet.avgEntryMC
                    ? <span className="sm-cell-empty">—</span>
                    : wallet.avgEntryMC}
                </span>
              </div>
              <div className="sm-col-duration">
                <span className="wallet-stat-text">
                  {wallet.avgHoldingDuration === '—' || !wallet.avgHoldingDuration
                    ? <span className="sm-cell-empty">—</span>
                    : wallet.avgHoldingDuration}
                </span>
              </div>
              <div className="sm-col-pnl">
                <span className="wallet-stat-text">
                  {wallet.pnl != null && !Number.isNaN(wallet.pnl)
                    ? wallet.pnl >= 0
                      ? `$${wallet.pnl.toLocaleString()}`
                      : `-$${Math.abs(wallet.pnl).toLocaleString()}`
                    : <span className="sm-cell-empty">—</span>}
                </span>
              </div>
              <div className="sm-col-winrate">
                <span className="wallet-stat-text">
                  {wallet.winRate != null && !Number.isNaN(wallet.winRate)
                    ? `${Number(wallet.winRate).toFixed(1)}%`
                    : <span className="sm-cell-empty">—</span>}
                </span>
              </div>
              <div className="sm-col-scalping">
                <span className="wallet-stat-text">
                  {wallet.scalpingCount != null && !Number.isNaN(wallet.scalpingCount)
                    ? wallet.scalpingCount.toLocaleString()
                    : <span className="sm-cell-empty">—</span>}
                </span>
              </div>
            </div>
            );
          })}
        </div>
        {hasMore && (
          <div className="smart-money-load-more-wrap">
            <button
              type="button"
              className="smart-money-load-more"
              onClick={() => setVisibleCount((prev) => Math.min(prev + ROWS_PER_PAGE, sortedTable.length))}
              aria-label="Load more rows"
            >
              +
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
